import { Link } from "@gromia/components/Link";
import * as s from "@gromia/components/theme/t.module.css";
import strings from "@gromia/strings/kUiPaginationBottom";
import { useParams } from "@gromia/utils/useParams";
import { useString } from "@gromia/utils/useString";
import cx from "classnames";
import { compact, range } from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import React from "react";
import * as spriv from "./PaginationBottom.module.css";

type Data = {
  count: number;
  page: number;
  pages: number;
  perPage: number;
  size: number; // count but for this page
};

type Props = {
  data: Data;
  pagination?: (n: number) => string;
};

function Sep() {
  return (
    <li className={spriv.pageItem}>
      <span className={cx(spriv.pageLink, spriv.disabled)}>…</span>
    </li>
  );
}

function Page(props: {
  page: number;
  current?: boolean;
  children: NonNullable<React.ReactNode>;
  className?: string;
  pagination?: (n: number) => string;
}) {
  const { search } = useParams();
  function defaultPagination(n: number) {
    const q = search({ page: n.toString() });
    return `${window.location.pathname}${q}`;
  }
  return (
    <li className={spriv.pageItem}>
      <Link
        className={cx(
          spriv.pageLink,
          props.current ? spriv.current : null,
          props.className,
        )}
        to={(props.pagination || defaultPagination)(props.page)}
      >
        {props.children}
      </Link>
    </li>
  );
}

type SepNode = { t: "sep" };
type PageNode = {
  t: "page";
  label: NonNullable<React.ReactNode>;
  value: number;
  className?: string;
};

type PaginationNode = PageNode | SepNode;

function page(
  value: number,
  label: NonNullable<React.ReactNode> = value,
  className?: string,
): PageNode {
  return { t: "page", label, value, className };
}

const sep: SepNode = { t: "sep" };

export function PaginationBottom(props: Props) {
  const { tx } = useString("kUiPaginationBottom", strings);

  const ctx = 3;
  const pad = (ctx - 1) / 2;
  const min = 1;
  const cur = props.data.page;
  const max = props.data.pages;

  // clamped middle indexes
  const start = Math.max(min, cur - pad);
  const end = Math.min(max, cur + pad);
  const middle = range(start, end);

  const xs: Array<O.Option<PaginationNode>> = [
    pipe(
      O.some(page(cur - 1, <span>&laquo;</span>)),
      O.filter(() => cur - 1 >= min),
    ),
    pipe(
      O.some(page(min, min, cx(s.dNone, s.dSmBlock))),
      O.filter(() => start > min),
    ),
    pipe(
      O.some(sep),
      O.filter(() => start > min + 1),
    ),
    ...middle.map(x => page(x)).map(O.some),
    pipe(
      O.some(sep),
      O.filter(() => end < max - 1),
    ),
    pipe(
      O.some(page(max, max, cx(s.dNone, s.dSmBlock))),
      O.filter(() => end < max),
    ),
    pipe(
      O.some(page(cur + 1, <span>&raquo;</span>)),
      O.filter(() => cur + 1 <= max),
    ),
  ];

  return (
    <div
      className={cx(
        s.spaceTop1,
        s.dFlex,
        s.flexColumn,
        s.flexSmRow,
        s.justifyContentSmBetween,
        s.alignItemsCenter,
      )}
    >
      <div className={cx(s.dFlex, s.flexRow, s.alignItemsCenter)}>
        <nav>
          <ul className={spriv.pagination}>
            {compact(xs).map((y, idx) =>
              y.t === "sep" ? <Sep key={idx} /> : (
                <Page
                  key={idx}
                  page={y.value}
                  current={y.value === cur}
                  className={y.className}
                  pagination={props.pagination}
                >
                  {y.label}
                </Page>
              )
            )}
          </ul>
        </nav>
      </div>
      <div className={cx(s.textMuted, s.small)}>
        {tx("page", {
          variables: { page: props.data.page, pages: props.data.pages },
        })}
      </div>
    </div>
  );
}
